import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Clock, HeartHandshake } from "lucide-react";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 fade-in-up">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-pp-forest mb-6">
              <span className="w-8 h-px bg-pp-forest" />
              Policy Provisions
            </span>

            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-pp-forest-deep">
              Life insurance,
              <br />
              <span className="italic text-pp-terracotta">simplified</span> for
              the people who matter.
            </h1>

            <p className="mt-8 text-lg lg:text-xl text-pp-text-secondary max-w-xl leading-relaxed">
              Compare term, whole, IUL and more in minutes. Guided by{" "}
              <span className="text-pp-forest-deep font-medium">
                Darryl Kelly
              </span>
              , an independent broker who works for you—not the carriers.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/quote">
                <Button
                  data-testid="hero-quote-btn"
                  className="bg-pp-terracotta hover:bg-pp-terracotta-dark text-white rounded-full px-10 py-6 text-base font-medium shadow-sm"
                >
                  Get my free quote
                </Button>
              </Link>
              <a href="#coverage">
                <Button
                  data-testid="hero-learn-btn"
                  variant="outline"
                  className="border-pp-forest text-pp-forest hover:bg-pp-forest hover:text-white rounded-full px-10 py-6 text-base font-medium bg-transparent"
                >
                  Explore coverage
                </Button>
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { Icon: Clock, label: "5 min quote" },
                { Icon: ShieldCheck, label: "Top-rated carriers" },
                { Icon: HeartHandshake, label: "No pressure, ever" },
              ].map(({ Icon, label }) => (
                <div key={label} className="flex flex-col items-start gap-2">
                  <Icon className="w-6 h-6 text-pp-forest stroke-[1.5]" />
                  <span className="text-sm text-pp-text-secondary">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative fade-in-up" style={{ animationDelay: "0.2s" }}>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-[0_24px_60px_rgba(26,46,38,0.18)]">
              <img
                src="https://images.pexels.com/photos/33769493/pexels-photo-33769493.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                alt="Family enjoying time together"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-pp-forest-deep/40 via-transparent to-transparent" />
            </div>

            <div
              data-testid="hero-floating-card"
              className="absolute -bottom-6 -left-6 sm:-left-10 bg-white rounded-2xl p-5 shadow-[0_16px_40px_rgba(26,46,38,0.12)] border border-pp-border-subtle max-w-[260px]"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-pp-cream flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-pp-forest" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-pp-text-secondary">
                    Average savings
                  </div>
                  <div className="font-sans font-semibold text-2xl text-pp-forest-deep tracking-tight">
                    $1,127<span className="text-sm text-pp-text-secondary font-normal">/yr</span>
                  </div>
                </div>
              </div>
              <p className="mt-3 text-xs text-pp-text-secondary leading-relaxed">
                Clients save by comparing 20+ carriers in one place.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
